import React, { useEffect, useState, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { AppKeys } from "../Shared/AppKeys";
import { storageHelper } from "../../Helpers/storageHelper";

const ExamTracker = ({ examId, timeLeft, tabSwitchCount, rawTabSwitchCount, onForceClose, isOnline, onConnectionStateChange }) => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const isFirstRender = useRef(true);

  // Initialize SignalR connection
  useEffect(() => {
    if (!isOnline) {
      if (connection) {
        connection.stop();
        setConnection(null);
      }
      return;
    }

    if (!connection) {
      // Create hub URL from API URL
      const hubUrl = AppKeys._API_PRODUCTION_URL.replace('/api/v1/', '/examHub');
      const token = storageHelper.getItem("token");

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => {
            const t = token || "";
            return t.replace(/^Bearer\s+/i, "");
          }
        })
        .withAutomaticReconnect()
        .build();

      newConnection.onreconnecting((error) => {
        setIsConnected(false);
        if (onConnectionStateChange) onConnectionStateChange(false);

      });

      newConnection.onreconnected((connectionId) => {
        setIsConnected(true);
        if (onConnectionStateChange) onConnectionStateChange(true);

      });

      newConnection.onclose((error) => {
        setIsConnected(false);
        if (onConnectionStateChange) onConnectionStateChange(false);

      });

      setConnection(newConnection);
    }
  }, [isOnline, connection]);

  // Start connection and register events
  useEffect(() => {
    if (connection) {
      connection.start()
        .then(() => {

          setIsConnected(true);
          if (onConnectionStateChange) onConnectionStateChange(true);

          connection.on("ForceCloseExam", (reason) => {
            if (onForceClose) {
              onForceClose(reason);
            }
          });

          // Join the exam session natively
          const request = {
            examId: examId,
            ExamId: examId,
            timeLeft: latestValues.current.timeLeft,
            TimeLeft: latestValues.current.timeLeft,
            tabSwitchCount: latestValues.current.tabSwitchCount,
            TabSwitchCount: latestValues.current.tabSwitchCount
          };
          connection.invoke("JoinExamSession", request)
            .catch(e => console.error("Error joining exam session via SignalR: ", e));
        })
        .catch(e => console.error("SignalR Connection Error: ", e));

      return () => {
        setIsConnected(false);
        if (onConnectionStateChange) onConnectionStateChange(false);
        connection.off("ForceCloseExam");
        connection.stop();
      };
    }
  }, [connection, onForceClose]);

  // Keep track of latest values for the interval
  const latestValues = useRef({ timeLeft, tabSwitchCount });
  useEffect(() => {
    latestValues.current = { timeLeft, tabSwitchCount };
  }, [timeLeft, tabSwitchCount]);

  // Send updates to the server INSTANTLY only when events occur (e.g. tab switch)
  const prevTabSwitchCount = useRef(rawTabSwitchCount);
  useEffect(() => {
    if (!isConnected || !connection || !examId) return;

    if (rawTabSwitchCount !== prevTabSwitchCount.current) {
      prevTabSwitchCount.current = rawTabSwitchCount;

      const request = {
        examId: examId,
        ExamId: examId,
        timeLeft: latestValues.current.timeLeft,
        TimeLeft: latestValues.current.timeLeft,
        tabSwitchCount: tabSwitchCount,
        TabSwitchCount: tabSwitchCount
      };

      connection.invoke("UpdateExamSession", request)
        .catch(e => console.error("Error updating tab switch instantly via SignalR: ", e));
    }
  }, [isConnected, connection, examId, rawTabSwitchCount, tabSwitchCount]);

  // Send continuous TimeLeft updates to the server every 10 seconds for live database tracking
  useEffect(() => {
    if (!isConnected || !connection || !examId) return;

    const intervalId = setInterval(() => {
      const request = {
        examId: examId,
        ExamId: examId,
        timeLeft: latestValues.current.timeLeft,
        TimeLeft: latestValues.current.timeLeft,
        tabSwitchCount: latestValues.current.tabSwitchCount,
        TabSwitchCount: latestValues.current.tabSwitchCount
      };

      connection.invoke("UpdateExamSession", request)
        .catch(e => console.error("Error sending continuous sync via SignalR: ", e));
    }, 1000); // 1-second interval for instant UI/DB syncing

    return () => clearInterval(intervalId);
  }, [isConnected, connection, examId]);

  return null; // This component doesn't render any UI
};

export default ExamTracker;
